import React, { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { RegisteredCheckBox } from '../../../../components/fields/CheckBox'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatInteger,
    formatMoney,
} from '../../../../packages/utils/numberFormatters'
import { FormTable, FormTableRow } from '../../issues/FormTable'
import { RegisteredDecimalField, RegisteredNumericField } from '../../../../components/fields/NumericField'
import { RegisteredDatePicker } from '../../../../components/fields/DatePicker'
import { isBefore } from 'date-fns'

type ActionForm = {
    actionType: string
    convertibleId: string
    id: string
    tempId?: string
    conversionPrice: string | null
    convertedShares: number | null
    repayment: string | null
    recordDate: string
}

type ActionFormWithId = ActionForm & { tempId?: string; id?: string }

const setTempId = (convertibleAction: ActionFormWithId) => {
    return {
        ...convertibleAction,
        tempId: convertibleAction.tempId || convertibleAction.id || uuidv4(),
    }
}

interface ActionDialogProps {
    convertible: ConvertibleEntity
    convertibleAction: ActionFormWithId | null
    handleSubmitConvertibleAction: (action: ActionForm) => void
    open: boolean
    handleClose: () => void
}

export function ActionDialog({
    convertible,
    convertibleAction,
    handleSubmitConvertibleAction,
    open,
    handleClose,
}: ActionDialogProps) {
    const formMethods = useForm<ActionForm>({
        defaultValues: {
            actionType: 'conversion',
            conversionPrice: null,
            convertedShares: null,
            repayment: null,
            recordDate: '',
        },
    })
    const [actionType, setActionType] = useState('conversion')

    const recordDateValue = formMethods.watch('recordDate')
    const repaymentValue = formMethods.watch('repayment')
    const conversionPriceValue = formMethods.watch('conversionPrice')
    const convertedSharesValue = formMethods.watch('convertedShares')

    useEffect(() => {
        if (convertibleAction) {
            formMethods.reset(convertibleAction)
            setActionType(convertibleAction.actionType)
        }
    }, [convertibleAction])

    const handleActionType = (event: SelectChangeEvent) => {
        setActionType(event.target.value)

        formMethods.setValue('actionType', event.target.value)
        if (event.target.value === 'conversion') {
            formMethods.setValue('conversionPrice', convertible.fixedConversionPrice || null)
        } else {
            formMethods.setValue('conversionPrice', null)
        }
        formMethods.setValue('convertedShares', null)
        formMethods.setValue('repayment', null)
        formMethods.setValue('recordDate', '')
    }

    const handleSubmit = () => {
        const data = formMethods.getValues()
        const newConvertibleAction = { ...convertibleAction, ...data }
        handleSubmitConvertibleAction(newConvertibleAction)
        handleClose()
    }

    const isConversionAction = actionType === 'conversion'

    const showRepaymentError =
        !isConversionAction && repaymentValue && convertible.loanAmount < parseFloat(repaymentValue)

    const isFormValid =
        formMethods.formState.isValid &&
        recordDateValue &&
        (repaymentValue || (conversionPriceValue && convertedSharesValue))

    return (
        <FormProvider {...formMethods}>
            <Dialog open={open} onClose={() => handleClose()}>
                <DialogTitle>Händelse</DialogTitle>
                <DialogContent>
                    <FormTable>
                        <FormTableRow label='Händelse'>
                            <FormControl fullWidth>
                                <InputLabel id='select-label'>Händelse</InputLabel>
                                <Select
                                    labelId='select-label'
                                    id='select'
                                    value={actionType}
                                    label='Händelse'
                                    onChange={handleActionType}
                                >
                                    <MenuItem value='conversion'>Konvertering</MenuItem>
                                    <MenuItem value='repayment'>Återbetalning</MenuItem>
                                    <MenuItem value='offset'>Kvittning</MenuItem>
                                </Select>
                            </FormControl>
                        </FormTableRow>
                        <FormTableRow label='Datum'>
                            <RegisteredDatePicker name='recordDate' label='Datum' fullWidth />
                        </FormTableRow>
                        {isConversionAction ? (
                            <>
                                <FormTableRow label='Konverteringskurs'>
                                    <RegisteredDecimalField
                                        label='Konverteringskurs'
                                        name='conversionPrice'
                                        disabled={!isConversionAction}
                                    />
                                </FormTableRow>
                                <FormTableRow label='Antal aktier'>
                                    <RegisteredNumericField
                                        label='Antal'
                                        name='convertedShares'
                                        disabled={!isConversionAction}
                                    />
                                </FormTableRow>
                            </>
                        ) : (
                            <FormTableRow label='Återbetalning'>
                                <RegisteredNumericField
                                    label='Återbetalning'
                                    name='repayment'
                                    disabled={isConversionAction}
                                />
                            </FormTableRow>
                        )}
                    </FormTable>
                    {showRepaymentError ? (
                        <Typography color='error' sx={{ pt: 2 }}>
                            Återbetalningen kan inte vara större än konvertibelns belopp
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Avbryt</Button>
                    <Button disabled={!isFormValid} onClick={handleSubmit}>
                        Spara
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}

function EventsFields({
    convertible,
    setIsCustomFormDirty,
}: {
    convertible: ConvertibleEntity
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const [actionFormOpen, setActionFormOpen] = useState(false)
    const [actionToEdit, setActionToEdit] = useState<ActionFormWithId | null>(null)
    const formMethods = useFormContext()
    const formValues = formMethods.getValues()
    const [convertibleActions, setConvertibleActions] = useState<ActionFormWithId[]>(
        ((formValues.convertibleActions as ActionFormWithId[]) || []).map(setTempId),
    )
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const handleCloseEventDialog = () => {
        setActionFormOpen(false)
    }

    const editFormOpen = (convertibleAction: ActionFormWithId) => {
        const { createdAt, updatedAt, ...rest } = convertibleAction
        setActionToEdit(rest)
        setActionFormOpen(true)
    }
    const newFormOpen = () => {
        setActionToEdit(null)
        setActionFormOpen(true)
    }

    const setFormConvertibleActions = useCallback(
        (a: ActionFormWithId[]) => {
            if (JSON.stringify(a) === JSON.stringify(convertibleActions)) return

            const newConvertibleActions = a.map(setTempId) // make sure all investors have a tempId
            setConvertibleActions(newConvertibleActions)
            // issue.investors = investors
            const formConvertibleActions = newConvertibleActions.map((p) => {
                const { tempId, ...rest } = p
                return rest
            })
            formMethods.setValue('convertibleActions', formConvertibleActions)
        },
        [formMethods, convertibleActions],
    )
    useEffect(() => {
        setFormConvertibleActions(convertibleActions)
    }, [convertibleActions, setFormConvertibleActions])

    const nullEmptyStrings = (obj: Record<string, unknown>) => {
        Object.keys(obj).forEach((key) => {
            if (obj[key] === '') obj[key] = null
        })
    }
    const handleSubmitConvertibleAction = (newConvertibleAction: ActionFormWithId) => {
        nullEmptyStrings(newConvertibleAction)
        setIsCustomFormDirty(true)
        if (newConvertibleAction.tempId) {
            // update
            const newConvertibleActions = convertibleActions.map((p) =>
                p.tempId === newConvertibleAction.tempId ? newConvertibleAction : p,
            )
            setFormConvertibleActions(newConvertibleActions)
        } else {
            // create
            newConvertibleAction.tempId = uuidv4()
            const newConvertibleActions = convertibleActions.concat(newConvertibleAction)
            setFormConvertibleActions(newConvertibleActions)
        }
    }

    const handleDelete = (convertibleAction: ActionFormWithId) => {
        const newConvertibleActions = convertibleActions.filter((p) => p.tempId !== convertibleAction.tempId)
        setFormConvertibleActions(newConvertibleActions)
    }

    if (!convertible) return null

    const totalAmount = convertibleActions.reduce((acc, curr) => {
        if (curr.repayment !== null) {
            acc -= +curr.repayment
        } else if (curr.convertedShares !== null && curr.conversionPrice !== null) {
            acc -= curr.convertedShares * parseFloat(curr.conversionPrice)
        }
        return acc
    }, convertible.loanAmount)

    const loanAmount = formMethods.watch('loanAmount')

    return (
        <Grid spacing={2} direction='row' container>
            <Grid size={12}>
                <RegisteredCheckBox
                    label='Konvertibel återbetald eller konverterad'
                    name='completed'
                    onChange={() => setIsCustomFormDirty(true)}
                />
            </Grid>
            <Grid size={12} sx={{ height: '100%' }}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='subtitle1' sx={{ py: 2 }}>
                        Händelser
                    </Typography>
                    <Button onClick={() => newFormOpen()}>Lägg till event</Button>
                </Stack>
                {actionFormOpen ? (
                    <ActionDialog
                        convertible={convertible}
                        convertibleAction={actionToEdit}
                        open={actionFormOpen}
                        handleClose={handleCloseEventDialog}
                        handleSubmitConvertibleAction={handleSubmitConvertibleAction}
                    />
                ) : null}
                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Datum</TableCell>
                                <TableCell>Händelse</TableCell>
                                <TableCell>Återbetalning</TableCell>
                                <TableCell>Konverteringskurs</TableCell>
                                <TableCell>Antal aktier</TableCell>
                                <TableCell width='90px'>Belopp</TableCell>
                                <TableCell width='80px' />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell height='51.75px'>
                                    {formatDateYYYYMMDD(convertible.publicationDate)}
                                </TableCell>
                                <TableCell>Upptagning av lån</TableCell>
                                <TableCell colSpan={3} />
                                <TableCell>
                                    <Typography variant='body2'>{formatBigMoney(loanAmount, { locale })}</Typography>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                            {convertibleActions
                                .sort((a, b) => (isBefore(new Date(a.recordDate), new Date(b.recordDate)) ? -1 : 1))
                                .map((convertibleAction) => {
                                    const { repayment, convertedShares, conversionPrice } = convertibleAction
                                    let amount = null
                                    if (repayment !== null) {
                                        amount = parseFloat(repayment)
                                    } else if (convertedShares !== null && conversionPrice !== null) {
                                        amount = convertedShares * parseFloat(conversionPrice)
                                    }
                                    return (
                                        <TableRow key={convertibleAction.tempId || convertibleAction.id}>
                                            <TableCell>{formatDateYYYYMMDD(convertibleAction.recordDate)}</TableCell>
                                            <TableCell>
                                                {t(`entities.convertible.fields.${convertibleAction.actionType}`)}
                                            </TableCell>
                                            <TableCell>
                                                {convertibleAction.repayment !== null
                                                    ? formatBigMoney(convertibleAction.repayment, { locale })
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {formatMoney(convertibleAction.conversionPrice, {
                                                    locale,
                                                    minDecimals: 2,
                                                    maxDecimals: 5,
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                {formatInteger(convertibleAction.convertedShares, { locale })}
                                            </TableCell>
                                            <TableCell>
                                                {amount !== null ? formatBigMoney(-amount, { locale }) : null}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction='row'>
                                                    <IconButton
                                                        onClick={() => {
                                                            editFormOpen(convertibleAction)
                                                        }}
                                                        sx={{ px: '4px' }}
                                                    >
                                                        <EditIcon color='warning' />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => {
                                                            handleDelete(convertibleAction)
                                                        }}
                                                        sx={{ px: '4px' }}
                                                    >
                                                        <DeleteIcon color='error' />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            <TableRow>
                                <TableCell height='51.75px' />
                                <TableCell>Summa</TableCell>
                                <TableCell colSpan={3} />
                                <TableCell>
                                    <Typography variant='body2'>{formatBigMoney(totalAmount, { locale })}</Typography>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    )
}

export default EventsFields
